import { CheckCircle2, Circle } from "lucide-react";
import { cn } from "@/lib/utils";

interface TimelineSection {
  id: string;
  title: string;
  steps: number;
  completed?: boolean;
}

interface ProgressTimelineProps {
  sections: TimelineSection[];
  currentSection: string;
  onSectionClick: (sectionId: string) => void;
}

export function ProgressTimeline({ sections, currentSection, onSectionClick }: ProgressTimelineProps) {
  return (
    <div className="sticky top-24 space-y-1">
      <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-4 px-3">
        Progress
      </h3>
      {sections.map((section, index) => {
        const isCurrent = section.id === currentSection;
        const isCompleted = section.completed;
        
        return (
          <button
            key={section.id}
            onClick={() => onSectionClick(section.id)}
            className={cn(
              "w-full text-left px-3 py-2 rounded-lg transition-all relative group",
              "hover:bg-accent hover:text-accent-foreground",
              isCurrent && "bg-primary/10 text-primary font-medium",
              !isCurrent && "text-muted-foreground"
            )}
          >
            <div className="flex items-center gap-3">
              {/* Icon */}
              {isCompleted ? (
                <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
              ) : (
                <Circle className={cn(
                  "h-5 w-5 flex-shrink-0",
                  isCurrent && "fill-primary text-primary"
                )} />
              )}
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {section.title}
                </div>
                <div className="text-xs text-muted-foreground">
                  {section.steps} {section.steps === 1 ? 'step' : 'steps'}
                </div>
              </div>
            </div>

            {/* Connector Line */}
            {index < sections.length - 1 && (
              <div className="absolute left-[22px] top-full h-4 w-0.5 bg-border" />
            )}
          </button>
        );
      })}
    </div>
  );
}
